/**
 * Todo module.
 *
 * Flag off (#96): lists the documents processed in this browser that had
 * conflicts (processed-documents.ts); a click reopens one like Recent does.
 *
 * Flag on (#140, VITE_FEATURE_COMPARE): the todos from Compare, read from the
 * proxy (CompareService.listTodos), one accordion item per document (#143,
 * all collapsed on entry) with tabs per state, Done/Dismiss/Reopen and Open
 * in Reader. '#/todo?document=<id>' shows one document's todos.
 */

import template from './todo.template.html?raw';
import type { AppModule, RouteParams } from '../../shell/module.types';
import type { TranslationParams } from '../../../i18n/ui-manager.types';
import type { ProcessedDocument } from '../../../core/processed-documents';
import type CompareService from '../../../core/compare-service';
import type { Todo, TodoState } from '../../../core/compare-service';
import type FilesService from '../../../core/files-service';
import { errorMessage } from '../../../core/forbidden-message';
import { locatePassage } from './locate-passage';

type Tab = TodoState | 'all';
const TABS: readonly Tab[] = ['open', 'done', 'dismissed', 'all'];

export interface TodoModuleDeps {
  t: (key: string, params?: TranslationParams) => string;
  onLanguageChange: (callback: () => void) => void;
  enabled: boolean;
  listProcessed: () => ProcessedDocument[];
  openDocument: (document: ProcessedDocument) => void;
  /** #140: VITE_FEATURE_COMPARE. */
  compareEnabled: boolean;
  getCompareService?: () => CompareService | null;
  getFilesService?: () => FilesService | null;
  openInReader?: (
    file: File,
    source?: { id: string; name: string }
  ) => Promise<void>;
  /** #140: the collection document open in the Reader, if any. */
  getReaderDocumentId?: () => string | null;
  navigate?: (path: string) => void;
}

export function createTodoModule(deps: TodoModuleDeps): AppModule {
  const { t } = deps;
  let root: HTMLElement | null = null;

  const withConflicts = (): ProcessedDocument[] =>
    deps.listProcessed().filter((document) => document.conflicts > 0);

  const row = (document: ProcessedDocument): HTMLLIElement => {
    const item = window.document.createElement('li');
    const button = window.document.createElement('button');
    button.type = 'button';
    button.className = 'todo__document';
    button.dataset.todoDocument = document.id;
    button.addEventListener('click', () => deps.openDocument(document));

    const name = window.document.createElement('span');
    name.className = 'todo__name';
    name.textContent = document.name;

    const conflicts = window.document.createElement('span');
    conflicts.className = 'todo__conflicts';
    conflicts.textContent = t('modules.todo.conflicts', {
      count: document.conflicts,
    });

    const processed = window.document.createElement('span');
    processed.className = 'todo__date';
    processed.textContent = t('modules.todo.processedAt', {
      date: new Date(document.processedAt).toLocaleDateString(),
    });

    button.append(name, conflicts, processed);
    item.append(button);
    return item;
  };

  const collection = deps.compareEnabled;
  let todos: Todo[] = [];
  let tab: Tab = 'open';
  let documentFilter: string | undefined;
  /** Expanded accordion items, kept across re-renders until the next entry. */
  const expanded = new Set<string>();
  let loadRun = 0;

  const $ = <T extends HTMLElement>(selector: string): T =>
    root!.querySelector<T>(selector)!;
  const say = (text: string): void => {
    $('[data-todo-message]').textContent = text;
  };
  const errorText = (error: unknown): string =>
    errorMessage(error, (key) => t(key), t('modules.todo.error'));
  const el = <K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className?: string,
    text?: string
  ): HTMLElementTagNameMap[K] => {
    const node = window.document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const inTab = (todo: Todo): boolean => tab === 'all' || todo.state === tab;

  const dateTimeOf = (iso: string): string =>
    new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  const action = (
    attribute: string,
    label: string,
    onClick: (button: HTMLButtonElement) => void
  ): HTMLButtonElement => {
    const button = el('button', 'btn btn-link', label);
    button.type = 'button';
    button.setAttribute(attribute, '');
    button.addEventListener('click', () => onClick(button));
    return button;
  };

  const pill = (state: TodoState): HTMLElement => {
    const node = el(
      'span',
      `todo__pill todo__pill--${state}`,
      t(`modules.todo.state.${state}`)
    );
    return node;
  };

  const todoRow = (todo: Todo): HTMLTableRowElement => {
    const tr = el('tr', 'todo__row');
    tr.dataset.todoRow = todo.id;

    const state = el('td');
    state.append(pill(todo.state));
    if (todo.isNew) {
      state.append(' ', el('span', 'todo__new', t('modules.todo.new')));
    }
    if (todo.dismissReason === 'auto') {
      state.append(
        ' ',
        el('span', 'todo__auto-dismissed', t('modules.todo.noLongerFound'))
      );
    }

    const passage = el('td', 'todo__passage', todo.passage.text);

    const counterpart = el('td', 'todo__counterpart');
    const label = todo.counterpart.country
      ? `${todo.counterpart.label} (${todo.counterpart.country})`
      : todo.counterpart.label;
    counterpart.append(label);
    if (todo.counterpart.removed) {
      counterpart.append(
        ' ',
        el('span', 'todo__removed', t('modules.todo.removed'))
      );
    }

    const kind = el('td', 'todo__kind', t(`modules.todo.kind.${todo.kind}`));

    const actions = el('td', 'todo__actions');
    actions.append(
      action(
        'data-todo-open',
        t('modules.todo.openInReader'),
        () => void openInReader(todo)
      )
    );
    if (todo.state !== 'done') {
      actions.append(
        action(
          'data-todo-done',
          t('modules.todo.done'),
          (button) => void setState(todo, 'done', button)
        )
      );
    }
    if (todo.state !== 'dismissed') {
      actions.append(
        action(
          'data-todo-dismiss',
          t('modules.todo.dismiss'),
          (button) => void setState(todo, 'dismissed', button)
        )
      );
    }
    if (todo.state !== 'open') {
      actions.append(
        action(
          'data-todo-reopen',
          t('modules.todo.reopen'),
          (button) => void setState(todo, 'open', button)
        )
      );
    }

    tr.append(state, passage, counterpart, kind, actions);
    return tr;
  };

  const COLUMNS = [
    'colState',
    'colPassage',
    'colCounterpart',
    'colKind',
    'colActions',
  ];

  /** #153: 'corpus' for the EU regulations, otherwise the counterpart document. */
  const sourceOf = (todo: Todo): string =>
    todo.counterpart.type === 'regulation'
      ? 'corpus'
      : (todo.counterpart.documentId ?? todo.counterpart.label);

  /** One source of a document's results: a heading that names its table. */
  const sourceSection = (
    key: string,
    own: [Todo, ...Todo[]],
    open: number,
    headingId: string
  ): HTMLElement => {
    const section = el('section', 'todo__source');
    section.dataset.todoSource = key;
    const heading = el('h3', 'todo__source-title');
    heading.id = headingId;
    const counterpart = own[0].counterpart;
    heading.append(
      el(
        'span',
        'todo__source-name',
        key === 'corpus' ? t('modules.todo.sourceCorpus') : counterpart.label
      )
    );
    if (key !== 'corpus' && counterpart.removed) {
      heading.append(el('span', 'todo__removed', t('modules.todo.removed')));
    }
    const count = el(
      'span',
      `badge rounded-pill todo__open-count${open > 0 ? '' : ' todo__open-count--none'}`,
      t('modules.todo.openCount', { count: open })
    );
    count.dataset.todoSourceOpen = '';
    heading.append(count);

    const table = el('table', 'table todo__table');
    table.setAttribute('aria-labelledby', headingId);
    const head = el('tr');
    head.append(
      ...COLUMNS.map((column) => {
        const th = el('th', undefined, t(`modules.todo.${column}`));
        th.scope = 'col';
        return th;
      })
    );
    const thead = el('thead');
    thead.append(head);
    const tbody = el('tbody');
    tbody.append(...own.map(todoRow));
    table.append(thead, tbody);
    section.append(heading, table);
    return section;
  };

  /**
   * One document as a Bootstrap accordion item. The header button toggles the
   * item itself (no data-bs-toggle) so the open state lives in `expanded` and
   * survives the re-render after Done/Dismiss/Reopen or a tab switch. Inside,
   * its results per source (#153): the EU corpus first, then each collection
   * document by name.
   */
  const groupItem = (
    first: Todo,
    visible: Todo[],
    open: number,
    index: number
  ): HTMLElement => {
    const id = first.documentId;
    const isOpen = expanded.has(id);
    const item = el('div', 'accordion-item');
    item.dataset.todoGroup = id;

    const header = el('h2', 'accordion-header');
    const button = el(
      'button',
      `accordion-button todo__group-toggle${isOpen ? '' : ' collapsed'}`
    );
    button.type = 'button';
    button.dataset.todoToggle = '';
    const panelId = `todo-group-${index}`;
    button.setAttribute('aria-controls', panelId);
    button.setAttribute('aria-expanded', String(isOpen));
    const count = el(
      'span',
      `badge rounded-pill todo__open-count${open > 0 ? '' : ' todo__open-count--none'}`,
      t('modules.todo.openCount', { count: open })
    );
    count.dataset.todoOpenCount = '';
    button.append(
      el('span', 'todo__group-name', first.documentName),
      el(
        'span',
        'todo__group-date',
        t('modules.todo.comparedAt', { date: dateTimeOf(first.comparedAt) })
      ),
      count
    );
    button.addEventListener('click', () => {
      if (expanded.has(id)) expanded.delete(id);
      else expanded.add(id);
      renderBoard();
    });
    header.append(button);

    const panel = el(
      'div',
      `accordion-collapse collapse${isOpen ? ' show' : ''}`
    );
    panel.id = panelId;
    const body = el('div', 'accordion-body todo__group-body');
    const bySource = new Map<string, [Todo, ...Todo[]]>();
    for (const todo of visible) {
      const key = sourceOf(todo);
      const own = bySource.get(key);
      if (own) own.push(todo);
      else bySource.set(key, [todo]);
    }
    const ordered = [...bySource.entries()].sort(([a, x], [b, y]) =>
      a === 'corpus'
        ? -1
        : b === 'corpus'
          ? 1
          : x[0].counterpart.label.localeCompare(y[0].counterpart.label)
    );
    body.append(
      ...ordered.map(([key, own], position) =>
        sourceSection(
          key,
          own,
          todos.filter(
            (todo) =>
              todo.documentId === id &&
              todo.state === 'open' &&
              sourceOf(todo) === key
          ).length,
          `${panelId}-source-${position}`
        )
      )
    );
    panel.append(body);

    item.append(header, panel);
    return item;
  };

  /** #150: '?document=<id>' narrows the list; say so and offer the way back. */
  const renderFilter = (): void => {
    $('[data-todo-filter]').hidden = !documentFilter;
    if (!documentFilter) return;
    const name = todos.find(
      (todo) => todo.documentId === documentFilter
    )?.documentName;
    $('[data-todo-filter-text]').textContent = name
      ? t('modules.todo.filter', { name })
      : t('modules.todo.filterUnnamed');
    $('[data-todo-show-all]').textContent = t('modules.todo.showAll');
  };

  const renderBoard = (): void => {
    renderFilter();
    const tabs = $('[data-todo-tabs]');
    tabs.replaceChildren(
      ...TABS.map((name) => {
        const count = todos.filter(
          (todo) => name === 'all' || todo.state === name
        ).length;
        const button = el(
          'button',
          `nav-link${name === tab ? ' active' : ''}`,
          `${t(`modules.todo.tab.${name}`)} ${count}`
        );
        button.type = 'button';
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', String(name === tab));
        button.dataset.todoTab = name;
        button.addEventListener('click', () => {
          tab = name;
          renderBoard();
        });
        return button;
      })
    );

    const visible = todos.filter(inTab);
    const byDocument = new Map<string, [Todo, ...Todo[]]>();
    for (const todo of visible) {
      const own = byDocument.get(todo.documentId);
      if (own) own.push(todo);
      else byDocument.set(todo.documentId, [todo]);
    }
    const groups = [...byDocument.values()].map((own, index) => {
      const open = todos.filter(
        (todo) => todo.documentId === own[0].documentId && todo.state === 'open'
      ).length;
      return groupItem(own[0], own, open, index);
    });
    const container = $('[data-todo-groups]');
    container.replaceChildren(...groups);
    container.hidden = visible.length === 0;
    const empty = $('[data-todo-board-empty]');
    empty.textContent = t('modules.todo.boardEmpty');
    empty.hidden = visible.length > 0;
  };

  async function load(): Promise<void> {
    const service = deps.getCompareService?.() ?? null;
    if (!service) return;
    const run = ++loadRun;
    try {
      const { items } = await service.listTodos(
        documentFilter ? { documentId: documentFilter } : {}
      );
      if (run !== loadRun) return;
      todos = items;
      say('');
    } catch (error) {
      if (run !== loadRun) return;
      todos = [];
      say(errorText(error));
    }
    renderBoard();
  }

  async function setState(
    todo: Todo,
    state: TodoState,
    button: HTMLButtonElement
  ): Promise<void> {
    const service = deps.getCompareService?.() ?? null;
    if (!service || button.disabled) return;
    button.disabled = true;
    try {
      const updated = await service.updateTodo(todo.id, state);
      todos = todos.map((item) => (item.id === todo.id ? updated : item));
      say('');
    } catch (error) {
      say(errorText(error));
    }
    renderBoard();
  }

  /**
   * Same path as Collection's Open (files download + openInReader; the Reader
   * refuses over 10 MB with its own message), then the passage is scrolled to
   * and flashed: the Reader has no in-document search, so locatePassage finds
   * the first rendered element holding the passage text. A document already
   * open in the Reader is not downloaded again: the Reader is shown and the
   * passage located.
   */
  async function openInReader(todo: Todo): Promise<void> {
    if (deps.navigate && deps.getReaderDocumentId?.() === todo.documentId) {
      deps.navigate('/reader');
      window.setTimeout(() => locatePassage(todo.passage.text), 300);
      return;
    }
    const files = deps.getFilesService?.() ?? null;
    if (!files || !deps.openInReader) return;
    say(t('modules.files.opening', { name: todo.documentName }));
    try {
      await deps.openInReader(await files.download(todo.documentId), {
        id: todo.documentId,
        name: todo.documentName,
      });
      say('');
      window.setTimeout(() => locatePassage(todo.passage.text), 300);
    } catch (error) {
      say(errorText(error));
    }
  }

  const render = (): void => {
    if (!root) return;
    if (collection) {
      renderBoard();
      return;
    }
    const list = root.querySelector<HTMLElement>('[data-todo-list]');
    const empty = root.querySelector<HTMLElement>('[data-todo-empty]');
    if (!list || !empty) return;
    const documents = withConflicts();
    list.replaceChildren(...documents.map(row));
    empty.hidden = documents.length > 0;
  };

  return {
    id: 'todo',
    route: '/todo',
    labelKey: 'modules.todo.label',
    descriptionKey: 'modules.todo.description',
    icon: 'todo',
    enabled: deps.enabled,
    showOnDashboard: true,

    mount(outlet) {
      outlet.innerHTML = template;
      root = outlet;
      if (collection) {
        $('[data-todo-board]').hidden = false;
        $('[data-todo-list]').hidden = true;
        $('[data-todo-empty]').hidden = true;
        const intro = $('.todo__intro');
        intro.removeAttribute('data-i18n');
        intro.textContent = t('modules.todo.introCompare');
        $('[data-todo-show-all]').addEventListener('click', () =>
          deps.navigate?.('/todo')
        );
      }
      deps.onLanguageChange(() => render());
    },

    onEnter(params?: RouteParams) {
      if (collection) {
        documentFilter = params?.query?.document;
        expanded.clear();
        render();
        void load();
        return;
      }
      render();
    },

    getStatus() {
      if (collection) {
        const open = todos.filter((todo) => todo.state === 'open').length;
        return open > 0 ? t('modules.todo.statusOpen', { count: open }) : null;
      }
      const count = withConflicts().length;
      return count > 0 ? t('modules.todo.status', { count }) : null;
    },
  };
}
